import React from 'react';
import { useParams } from 'react-router-dom';
import { FiMenu, FiImage, FiSend, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { resizeImageToDataUrl } from '../utils/image';
import { renderMarkdown } from '../utils/markdown';
import { playBeep } from '../utils/beep';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const ChatGuest = () => {
  const { publicId } = useParams();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const [chat, setChat] = React.useState(null);
  const [guest, setGuest] = React.useState(null);
  const [guestName, setGuestName] = React.useState('');
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [messageText, setMessageText] = React.useState('');
  const [imagePreview, setImagePreview] = React.useState('');
  const [isProcessingImage, setIsProcessingImage] = React.useState(false);
  const [messages, setMessages] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [guestToken, setGuestToken] = React.useState('');
  const [galleryIndex, setGalleryIndex] = React.useState(0);
  const [isImageViewerOpen, setIsImageViewerOpen] = React.useState(false);
  const messagesEndRef = React.useRef(null);

  const fetchJson = async (path, options = {}) => {
    const response = await fetch(`${apiBaseUrl}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }
    return data;
  };

  React.useEffect(() => {
    const storedName = localStorage.getItem(`guestName:${publicId}`) || '';
    setGuestName(storedName);
  }, [publicId]);

  React.useEffect(() => {
    if (guest?.displayName && !guestName) {
      setGuestName(guest.displayName);
    }
  }, [guest, guestName]);

  React.useEffect(() => {
    const loadChat = async () => {
      try {
        const data = await fetchJson(`/chats/public/${publicId}`);
        setChat(data.chat);
      } catch (error) {
        toast.error(error.message || 'Chat not found');
        setIsLoading(false);
      }
    };
    loadChat();
  }, [publicId]);

  React.useEffect(() => {
    if (!chat) {
      return;
    }
    const enterChat = async () => {
      try {
        const data = await fetchJson(`/chats/public/${publicId}/enter`, {
          method: 'POST',
          body: JSON.stringify({})
        });
        setGuest(data.guest);
        setGuestToken(data.guestToken || '');
        localStorage.setItem(`guestId:${publicId}`, data.guest.id);
      } catch (error) {
        toast.error(error.message || 'Failed to enter chat');
        setIsLoading(false);
      }
    };
    enterChat();
  }, [chat, publicId]);

  const loadMessages = React.useCallback(async () => {
    if (!guest?.id) {
      return;
    }
    try {
      const data = await fetchJson(`/chats/public/${publicId}/messages?guestId=${encodeURIComponent(guest.id)}`);
      setMessages(data.messages || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  }, [guest, publicId]);

  React.useEffect(() => {
    if (!guest?.id) {
      return;
    }
    loadMessages();
  }, [guest, loadMessages]);

  React.useEffect(() => {
    if (!guestToken || !guest?.id) {
      return undefined;
    }

    const base = process.env.REACT_APP_WS_URL || apiBaseUrl.replace(/\/api\/?$/, '');
    const wsUrl = `${base.replace(/^http/, 'ws')}/ws?token=${encodeURIComponent(guestToken)}`;
    let socket;
    let reconnectTimer;
    let isActive = true;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'chat_message' && data.chatId === publicId && data.guestId === guest.id) {
          if (data.senderType === 'admin') {
            playBeep();
          }
          loadMessages();
        }
      } catch (error) {
        // ignore parse errors
      }
    };

    const connect = () => {
      socket = new WebSocket(wsUrl);
      socket.onmessage = handleMessage;
      socket.onclose = () => {
        if (!isActive) {
          return;
        }
        reconnectTimer = setTimeout(connect, 2000);
      };
      socket.onerror = () => {
        socket.close();
      };
    };

    connect();

    return () => {
      isActive = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (socket) {
        socket.close();
      }
    };
  }, [guestToken, guest, publicId, apiBaseUrl, loadMessages]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  React.useEffect(() => {
    if (chat?.descriptionImages?.length && galleryIndex >= chat.descriptionImages.length) {
      setGalleryIndex(0);
    }
  }, [chat, galleryIndex]);

  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      setIsProcessingImage(true);
      const dataUrl = await resizeImageToDataUrl(file, { maxDimension: 1280, quality: 0.8 });
      setImagePreview(dataUrl);
    } catch (error) {
      toast.error('Failed to process image');
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleSaveName = async () => {
    if (!guest?.id) {
      return;
    }
    try {
      const trimmed = guestName.trim().slice(0, 60);
      const data = await fetchJson(`/chats/public/${publicId}/guest`, {
        method: 'PUT',
        body: JSON.stringify({ guestId: guest.id, displayName: trimmed })
      });
      setGuest((prev) => ({ ...prev, displayName: data.guest.displayName }));
      localStorage.setItem(`guestName:${publicId}`, trimmed);
      setIsMenuOpen(false);
      toast.success('Name updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update name');
    }
  };

  const handleSend = async () => {
    if (!guest?.id) {
      return;
    }
    if (!messageText.trim() && !imagePreview) {
      return;
    }
    try {
      if (messageText.trim()) {
        await fetchJson(`/chats/public/${publicId}/messages`, {
          method: 'POST',
          body: JSON.stringify({
            guestId: guest.id,
            messageType: 'text',
            content: messageText.trim()
          })
        });
        setMessageText('');
      }

      if (imagePreview) {
        await fetchJson(`/chats/public/${publicId}/messages`, {
          method: 'POST',
          body: JSON.stringify({
            guestId: guest.id,
            messageType: 'image',
            content: imagePreview
          })
        });
        setImagePreview('');
      }
      loadMessages();
    } catch (error) {
      toast.error(error.message || 'Failed to send message');
    }
  };

  const chatTitle = chat?.title || 'Chat';
  const guestLabel = guest?.displayName || guestName || 'Guest';
  const galleryImages = chat?.descriptionImages || [];
  const hasGallery = galleryImages.length > 0;
  const showDescriptionPanel = !!chat?.description || hasGallery;

  return (
    <div
      className="h-[100dvh] overflow-hidden bg-gray-900 text-white relative"
      style={
        chat?.backgroundImage
          ? {
              backgroundImage: `url(${chat.backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }
          : undefined
      }
    >
      <div className={`absolute inset-0 ${chat?.backgroundImage ? 'bg-black/50' : 'bg-slate-900'}`} />

      <div className="relative z-10 h-full flex flex-col">
        <header className="flex items-center justify-between px-4 py-2 border-b border-white/10">
          <div>
            <h1 className="text-lg font-semibold">{chatTitle}</h1>
            <p className="text-xs text-white/70">Guest chat</p>
          </div>
          <button
            type="button"
            onClick={() => setIsMenuOpen(true)}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20"
          >
            <FiMenu className="h-5 w-5" />
          </button>
        </header>

        <main className="flex-1 min-h-0 px-4 py-4 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {showDescriptionPanel && (
                <div className="mb-4 rounded-lg bg-white/10 p-3 text-sm text-white/90">
                  {chat?.description ? (
                    <div className="space-y-2">
                      <div dangerouslySetInnerHTML={{ __html: renderMarkdown(chat.description) }} />
                    </div>
                  ) : (
                    <p className="text-xs text-white/70">Chat gallery available.</p>
                  )}
                  {hasGallery && (
                    <div className="mt-3 rounded-lg bg-black/20 p-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                          className="rounded-full bg-white/10 p-2 hover:bg-white/20 disabled:opacity-50"
                          disabled={galleryImages.length <= 1}
                          aria-label="Previous image"
                        >
                          <FiChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsImageViewerOpen(true)}
                          className="flex-1 flex justify-center"
                        >
                          <img
                            src={galleryImages[galleryIndex]}
                            alt={`Chat gallery ${galleryIndex + 1}`}
                            className="max-h-40 w-full rounded-md object-contain"
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() => setGalleryIndex((prev) => (prev + 1) % galleryImages.length)}
                          className="rounded-full bg-white/10 p-2 hover:bg-white/20 disabled:opacity-50"
                          disabled={galleryImages.length <= 1}
                          aria-label="Next image"
                        >
                          <FiChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="mt-2 text-center text-xs text-white/70">
                        {galleryIndex + 1} / {galleryImages.length}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {messages.length === 0 ? (
                <p className="text-sm text-white/70">No messages yet. Start the conversation.</p>
              ) : (
                <div className="space-y-3">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.senderType === 'guest' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                          message.senderType === 'guest' ? 'bg-white text-gray-900' : 'bg-white/10 text-white'
                        }`}
                      >
                        {message.messageType === 'image' ? (
                          <img src={message.content} alt="Chat upload" className="rounded-md max-h-64" />
                        ) : (
                          <p>{message.content}</p>
                        )}
                        <p className={`mt-1 text-[10px] ${message.senderType === 'guest' ? 'text-gray-500' : 'text-white/70'}`}>
                          {message.senderType === 'guest' ? guestLabel : 'Admin'} -{' '}
                          {new Date(message.createdAt).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </>
          )}
        </main>

        <footer className="shrink-0 border-t border-white/10 px-4 py-4 bg-black/20">
          {imagePreview && (
            <div className="mb-3">
              <div className="relative inline-block">
                <img src={imagePreview} alt="Preview" className="h-20 w-28 object-cover rounded-md border border-white/20" />
                <button
                  type="button"
                  onClick={() => setImagePreview('')}
                  className="absolute -top-2 -right-2 bg-white text-gray-700 rounded-full p-1"
                >
                  <FiX className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-md cursor-pointer">
              <FiImage className="h-4 w-4" />
              <span className="text-sm">Image</span>
              <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
            </label>
            <input
              value={messageText}
              onChange={(event) => setMessageText(event.target.value)}
              placeholder="Type your message..."
              className="flex-1 min-w-[200px] rounded-md bg-white/10 border border-white/20 px-3 py-2 text-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={isProcessingImage}
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-100"
            >
              <FiSend className="h-4 w-4" />
              Send
            </button>
          </div>
        </footer>
      </div>

      {isMenuOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40">
          <div className="bg-white text-gray-900 w-full max-w-sm h-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Guest settings</h2>
              <button type="button" onClick={() => setIsMenuOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display name</label>
            <input
              value={guestName}
              onChange={(event) => setGuestName(event.target.value)}
              className="input-field mb-4"
              maxLength={60}
            />
            <button type="button" className="btn-primary w-full" onClick={handleSaveName}>
              Save name
            </button>
          </div>
        </div>
      )}

      {isImageViewerOpen && hasGallery && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4">
          <button
            type="button"
            onClick={() => setIsImageViewerOpen(false)}
            className="absolute top-4 right-4 text-white/70 hover:text-white"
          >
            <FiX className="h-6 w-6" />
          </button>
          <img
            src={galleryImages[galleryIndex]}
            alt={`Chat gallery full ${galleryIndex + 1}`}
            className="max-h-[90vh] max-w-full rounded-lg object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default ChatGuest;
